import React, { useEffect, Fragment, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { returnInTZ } from "./utils/timezone";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import {
  useToast,
} from "@chakra-ui/react";
import { expiredLogout } from "./store/auth/thunks";
import { getUserFromSession, getProfilePicture, getPreferences, getAccountValues } from "./store/user/thunks";
import { getTradesPage, getTradesStats, getTradesStatsFiltered } from "./store/trades/thunks";
import { getJournalEntries } from "./store/journal/thunks";
import Home from "./components/Home";
import PnlCalendar  from "./components/PnlCalendar";
import Login from "./components/Login";
import Journal from "./components/Journal";
import Leaderboard from "./components/Leaderboard";
import Groups from "./components/Groups";
import Signup from "./components/Signup";
import LogTrade from "./components/LogTrade";
import Navbar from "./components/Navbar";
import UserProfile from "./components/UserProfile";
import Summary from "./components/Summary";
import ResetPass from "./components/ResetPass";
import { TOKEN } from "./api/axiosInstance";
import LandingPage from "./components/LandingPage";
import Payment from "./components/Payment";
import TickerAnalytics from "./components/TickerAnalytics";
import TagAnalytics from "./components/TagAnalytics";
import EmotionalAnalytics from "./components/EmotionalAnalytics";
import Insights from "./components/Insights";
import OptionsAnalytics from "./components/OptionsAnalytics";
import TrialToast from "./components/TrialToast";
import AskPayment from "./components/AskPayment";
import PublicProfile from "./components/PublicProfile";
import MyFeedback from "./components/MyFeedback";
import Study from "./components/Study";


export default function App() {
  const [toastMessage, setToastMessage] = useState(undefined);
  const toast = useToast();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const { info } = useSelector((state) => state.ui);
  const { trades } = useSelector((state) => state.trades);
  const { pnlYTD } = useSelector((state) => state.analytics);
  const { stats } = useSelector((state) => state.trades);
  const { profilepic } = useSelector((state) => state.user);
  const authError = useSelector((state) => state.ui.error);
  const tradeError = useSelector((state) => state.trade.error);
  const tradeInfo = useSelector((state) => state.trade.info);
  const isLoggedIn = (((user && Object.keys(user).length > 2) || window.localStorage.getItem(TOKEN)) ? (true):(false));
  const [registered, setRegistered] = useState(true);
  const isRegistered = ((info && Object.keys(info).length > 2 && info.result && info.result === "User Created Successfully") ? (true):(false));
  const [deleted, setDeleted] = useState(true);
  const isDeleted = ((info && Object.keys(info).length === 1 && info.result && info.result === "User Successfully Deleted") ? (true):(false));
  const [reset, setReset] = useState(true);
  const isReset = ((info && Object.keys(info).length === 1 && info.result && info.result === "Password Reset Successfully") ? (true):(false));
  const hasTrades = ((trades && trades.trades && Object.keys(trades.trades).length > 0) ? (true):(false));
  const noTrades = ((trades && trades.trades && Object.keys(trades.trades).length === 0) ? (true):(false)); 
  const hasStats = ((stats && stats.stats && Object.keys(stats.stats).length > 0) ? (true):(false));
  const hasProfilePicture = ((profilepic && Object.keys(profilepic).length > 0) ? (true):(false)); 
  
  if(isRegistered === true && registered === true){
    const savedUserInfo = window.localStorage.getItem('userInfo');
    if (savedUserInfo) {
      window.localStorage.removeItem('userInfo');
    }
    setToastMessage(info.result);
    setRegistered(false);
  }

  const [promptPayment, setPromptPayment] = useState(false);

  useEffect(() => {
    if (isLoggedIn === true && user?.user_id !== undefined && user?.has_paid === 0) {
      setPromptPayment(true);
    } else if (isLoggedIn === false){
      setPromptPayment(false);
    }
  }, [isLoggedIn, user]);
  

  if(isDeleted === true && deleted === true){
    setToastMessage(info.result);
    setDeleted(false);
  }

  if(isReset === true && reset === true){
    setToastMessage(info.result);
    setReset(false);
  }

  useEffect(() => {
    evaluateError();
  }, [authError, tradeError]); 

  const handleDeleteLocal = () => {
    window.localStorage.removeItem('userInfo');
    window.localStorage.removeItem('journalInfo');
    window.localStorage.removeItem('tradeInfo');
    window.localStorage.removeItem('feedbackInfo');
    window.localStorage.removeItem('updateTradeInfo');
    window.localStorage.removeItem('updateBulkTradeInfo');
    window.localStorage.removeItem('updateUserInfo');
    window.localStorage.removeItem('HomeFilters');
    window.localStorage.removeItem('CalendarFilters');
    window.localStorage.removeItem('SummaryFilters');
    window.localStorage.removeItem('LeaderboardFilters');
  }

  const evaluateError = async () => {
    if(authError === true && info?.response?.data?.result === "Auth Token Has Expired"){
      handleDeleteLocal();
      await dispatch(expiredLogout());
    }
    if(tradeError === true && tradeInfo.response.data.result === "Auth Token Has Expired"){
      handleDeleteLocal();
      await dispatch(expiredLogout());
    }
  }

  const today = returnInTZ(new Date().toISOString());

  function loadFiltersFromLocalStorage(page) {
    const filters = localStorage.getItem(page+'Filters');
    return filters ? JSON.parse(filters) : {};
  }

  function loadInfoFromLocalStorage(page) {
    const filters = localStorage.getItem(page+'Info');
    return filters ? JSON.parse(filters) : {};
  }

  
  useEffect(() => {
    async function getUserTrades(){
      if(isLoggedIn && user.user_id && !hasProfilePicture){
        await dispatch(getProfilePicture());
      }
      if(isLoggedIn && !hasTrades && !noTrades && user.user_id && !hasStats){
        if (window.location.pathname === "/home" || window.location.pathname === "/"){
          let filters = loadFiltersFromLocalStorage('Home');
          let hasFilters = Object.keys(filters).length > 0;
          if (hasFilters) {
            await dispatch(getTradesStatsFiltered({ filters }));
          } else {
            await dispatch(getTradesStats());
          }
          await dispatch(getPreferences());
          filters = {};
          filters.date = today
          await dispatch(getAccountValues({ filters }));
          filters = loadFiltersFromLocalStorage('Home');
          hasFilters = Object.keys(filters).length > 0;
          if (hasFilters) {
            filters.page = 1;
            filters.numrows = 5;
            filters.trade_date = 'NULL';
            await dispatch(getTradesPage({ filters }));
          } else {
            filters = {};
            filters.page = 1;
            filters.numrows = 5;
            filters.trade_date = 'NULL';
            await dispatch(getTradesPage({ filters }));
          }
        }else if (window.location.pathname === "/journal"){
          let info = loadInfoFromLocalStorage('journalInfo');
          let hasInfo = Object.keys(info).length > 0;
          let date = today;
          if (hasInfo && info.selectedDate) {
            date = info.selectedDate;
          }
          await dispatch(getJournalEntries({ date })); 
          // Update the journalInfo with the API call date
          const updatedJournalInfo = {
            ...info,
            selectedDate: date,
            source: info.source || 'app',
            lastApiCall: new Date().toISOString()
          };
          window.localStorage.setItem('journalInfo', JSON.stringify(updatedJournalInfo));
          // Inform Journal page of the selected date to sync state without remount
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('journalNavigate', { detail: { selectedDate: date, source: 'app' } }));
          }
          await dispatch(getPreferences());
        }else if (window.location.pathname === "/profile"){
          await dispatch(getPreferences());
        }else{
          let filters = loadFiltersFromLocalStorage('Summary');
          let hasFilters = Object.keys(filters).length > 0;
          if (hasFilters) {
            await dispatch(getTradesPage({ filters }));
          } else {
            filters = {};
            filters.page = 1;
            filters.numrows = 100;
            await dispatch(getTradesPage({ filters }));
          }
          await dispatch(getPreferences());
        }
      }else if (isLoggedIn && user.user_id === undefined){
        await dispatch(getUserFromSession());
      }
    }
    getUserTrades();
  }, [isLoggedIn,hasTrades,user,hasStats]);

  useEffect(() => {
    if (toastMessage) {
      toast({
        title: toastMessage,
        variant: 'solid',
        status: 'success',
        duration: 10000,
        isClosable: true
      });
    }
    setToastMessage(undefined);
  }, [toastMessage, toast]);

  return (
    <Fragment>
      <Router>
        <Navbar user={user} />
        <TrialToast/>
        <Routes>
          {isLoggedIn ? (
            <>
              {promptPayment ? (
                <>
                  <Route path="/" element={<Navigate to="/upgradeaccount"/>} />
                  <Route path="/login" element={<Navigate to="/upgradeaccount"/>} />
                  <Route path="/upgradeaccount" element={<AskPayment promptPayment={promptPayment} setPromptPayment={setPromptPayment}/>} />
                </>
              ): (
                <>
                  <Route path="/" element={<Navigate to="/home"/>} />
                  <Route path="/login" element={<Navigate to="/"/>} />
                </>
              )}
              <Route path="/home" element={<Home user={user}/>} />
              <Route path="/PnlCalendar" element={<PnlCalendar user={user} pnlYTD={pnlYTD}/>} />
              <Route path="/journal" element={<Journal user={user}/>} />
              <Route path="/leaderboard" element={<Leaderboard user={user}/>} />
              <Route path="/groups" element={<Groups user={user}/>} />
              <Route path="/resetpassword" element={<Navigate to="/"/>} />
              <Route path="/signup" element={<Navigate to="/"/>} />
              <Route path="/logTrade" element={<LogTrade user={user}/>} />
              <Route path='/profile' element={<UserProfile user={user}/>} />
              <Route path='/summary' element={<Summary user={user} trades={trades}/>} />
              <Route path='/payment' element={<Payment promptPayment={promptPayment} setPromptPayment={setPromptPayment}/>} />
              <Route path='/ticker-analytics' element={<TickerAnalytics user={user}/>} />
              <Route path='/tag-analytics' element={<TagAnalytics user={user}/>} />
              <Route path='/emotional-analytics' element={<EmotionalAnalytics user={user}/>} />
              <Route path='/insights' element={<Insights user={user}/>} />
              <Route path='/options-analytics' element={<OptionsAnalytics user={user}/>} />
              <Route path="/u/:username" element={<PublicProfile />} />
              <Route path="/myfeedback" element={<MyFeedback />} />
              <Route path="/study" element={<Study user={user} />} />
              <Route path="/study/:ticker" element={<Study user={user} />} />
            </>
          ) : (
            <>
              <Route path="/" element={<LandingPage />} />
              <Route path="/home" element={<Navigate to="/login" />} />
              <Route path="/PnlCalendar" element={<Navigate to="/login" />} />
              <Route path="/journal" element={<Navigate to="/login" />} />
              <Route path="/leaderboard" element={<Navigate to="/login" />} />
              <Route path="/groups" element={<Navigate to="/login" />} />
              <Route path="/profile" element={<Navigate to="/login"/>} />
              <Route path="/logTrade" element={<Navigate to="/login"/>} />
              <Route path='/summary' element={<Navigate to="/login"/>} />
              <Route path='/ticker-analytics' element={<Navigate to="/login"/>} />
              <Route path='/tag-analytics' element={<Navigate to="/login"/>} />
              <Route path='/insights' element={<Navigate to="/login"/>} />
              <Route path='/options-analytics' element={<Navigate to="/login"/>} />
              <Route path="/study" element={<Navigate to="/login"/>} />
              <Route path="/study/:ticker" element={<Navigate to="/login"/>} />
              <Route path="/login" element={<Login />} />
              <Route path="/resetpassword" element={<ResetPass />} />
              <Route path='/payment' element={<Payment promptPayment={promptPayment} setPromptPayment={setPromptPayment}/>} />
              <Route path="/u/:username" element={<PublicProfile />} />
              <Route path="/myfeedback" element={<Navigate to="/login" />} />
              {isRegistered ? (
                <Route path="/signup" element={<Navigate to="/login"/>} />
              ) : (
                <Route path="/signup" element={<Signup />} />
              )}
            </>
          )}
        </Routes>
      </Router>
    </Fragment>
  );
}